import { FC, useEffect, useState } from 'react';
import CodeEditorHeader, {
	ResultTabs,
} from '../CodeEditorHeader/CodeEditorHeader';
import {
	getFilesEditorHeight,
	getResultBlockHeight,
} from '../codeEditor.helper';
import getJsCodeForEvaluation from '../jsIframeCodeGenerator';
import { IEntityContentField } from '../../../../lib/interfaces/Common';
import JsOnlyResult, {
	JsResultType,
	ResultMode,
} from '../../CodeExample/JsOnlyResult';
import './CodeResult.scss';
import JsTestsResult from './JsTestsResult';

type CodeType = {
	css?: string;
	html?: string;
	js?: string;
	scss?: string;
	jsTests?: string;
};

const getScssScript = (scssCode: string, fieldName: string) => {
	const scssStrigified = scssCode
		.replace(/\/\*[\s\S]*?\*\/|(?<=[^:])\/\/.*|^\/\/.*/g, '')
		.replace(/\n/g, ' ');
	const fieldNameString = fieldName;
	const sassScript = scssCode
		? `
		<script id="" src="https://cdnjs.cloudflare.com/ajax/libs/sass.js/0.11.1/sass.sync.min.js"></script>
		<script>
			const compileScss = function() {
				const scssCode = '${scssStrigified}';
				Sass.compile(scssCode, function(result) {
					if(result && result.text) {
						var css = result.text,
						head = document.head || document.getElementsByTagName('head')[0],
						style = document.createElement('style');
						head.appendChild(style);

						var compiledScssCode = { compiledScss: result.text };
						var emitEvent = new CustomEvent('compiledScss_${fieldNameString}', { detail: compiledScssCode });
						window.parent.document.dispatchEvent(emitEvent);

						style.type = 'text/css';
						if (style.styleSheet){
							style.styleSheet.cssText = css;
						} else {
							style.appendChild(document.createTextNode(css));
						}
					}
				});
			}
			compileScss();
		</script>`
		: '';
	return sassScript;
};

const getCodeEmbedString = (code: CodeType, fieldName: string) => {
	let string = '<style>*{font-family: Arial, sans-serif;}</style>';
	if (code?.html) {
		string = `${string}${code.html}`;
	} else {
		delete code.html;
	}
	if (code?.css) {
		string = `${string}<style>${code.css}</style>`;
	} else {
		delete code.css;
	}
	if (code?.js) {
		const jsCode = getJsCodeForEvaluation({
			code: code.js,
			name: fieldName,
			tests: code.jsTests || null,
		});
		string = `${string}<script>${jsCode}</script>`;
	}
	if (code?.scss) {
		string = `${string}${getScssScript(code.scss, fieldName)}`;
	} else {
		delete code.scss;
	}
	// js
	return string;
};

type CodeResultProps = {
	code: CodeType;
	isHorizontalView: boolean;
	editorsNumber: number;
	hideIframe: boolean;
	hideFrame: boolean;
	iframeHeight: number;
	field: IEntityContentField;
	isCodeExample: boolean;
	jsResults?: JsResultType;
	isCodeEditorWithFiles?: boolean;
	isExpanded?: boolean;
};

const CodeResult: FC<CodeResultProps> = ({
	code,
	isHorizontalView,
	editorsNumber,
	hideIframe,
	hideFrame,
	iframeHeight,
	field,
	jsResults,
	isCodeExample = false,
	isCodeEditorWithFiles = false,
	isExpanded = false,
}) => {
	const fieldName = field.name;
	const iframeWraperId = `cwt_result_${fieldName}`;
	const iframeId = `cwt_res_iframe_${fieldName}`;
	const defaultTab: ResultTabs = field?.options?.jsOutputByDefault
		? ResultTabs.JS_OUTPUT
		: ResultTabs.RESULT;

	const [resultTab, setResultTab] = useState<ResultTabs>(defaultTab);
	const [jsTestsStored] = useState(code.jsTests);

	const resizeIframe = () => {
		// automatic iframe resize according to inner content
		const iframeEl = document.getElementById(iframeId) as HTMLIFrameElement;
		if (isCodeExample && iframeEl) {
			const actualHeight =
				iframeEl?.contentWindow?.document.documentElement.scrollHeight;
			if (actualHeight) {
				iframeEl.setAttribute('height', actualHeight.toString());
			}
		}
	};

	const onLoadIframe = () => {
		// if we don't have iframeHeight set from the dashbord
		// if it is coming from CodeExample component (and not CodeEditor)
		// TODO: think how to make it for code editor in task embedded mode only
		if (!iframeHeight && isCodeExample) {
			// need to set timeout because of rendering order
			// it works with shorter timeout but even 200 doesn't affect performance too much
			// leaving 200 to make sure it works
			setTimeout(resizeIframe, 200);
		}
	};

	useEffect(() => {
		const iframeHeightValue = () => {
			if (iframeHeight) {
				return iframeHeight;
			}
			if (isCodeExample) {
				return 10; // just a default value
			}
			if (isCodeEditorWithFiles) {
				return getFilesEditorHeight(isExpanded, true);
			}
			return getResultBlockHeight({
				isHorizontalView,
				editorsNumber,
			});
		};

		if (iframeWraperId) {
			const iframeWraperEl = document.getElementById(iframeWraperId);
			(
				iframeWraperEl as HTMLElement
			).innerHTML = `<iframe id="${iframeId}" class="cwt-res-iframe" height="${iframeHeightValue()}" onload="${onLoadIframe()}"></iframe>`;
			if (jsTestsStored) {
				code.jsTests = jsTestsStored;
			}
			const codeString = getCodeEmbedString(code, fieldName);
			const iframeEl = document.querySelector(`#${iframeId}`);
			(iframeEl as HTMLIFrameElement)?.contentDocument?.write(codeString);
		}
	}, [code, isExpanded]);

	const [jsTabResults, setJsTabResults] = useState(null);

	useEffect(() => {
		window.addEventListener('message', (e) => {
			const { data } = e;
			if (data && data.source && data.source === `__cwt__jstests__${field.name}`) {
				setJsTabResults(data.payload);
			}
		});
	}, []);

	const handleOnResultTabChange = (tab: ResultTabs) => {
		setResultTab(tab);
	};

	const isJsOnly =
		(Object.keys(code).length === 1 && !!code.js) ||
		(Object.keys(code).length === 2 && !!code.js && !!code.jsTests);
	const hasJsTests = !!jsResults?.tests?.length;

	return (
		<div className="CodeResult">
			{(!hideFrame || !!jsTabResults) && (
				<CodeEditorHeader
					name="Result"
					theme={jsTabResults ? 'dark' : 'dark'}
					jsConsoleButton={!!jsResults || !!jsTabResults}
					jsTestsButton={hasJsTests}
					isResult
					defaultTab={defaultTab}
					onResultTabChange={handleOnResultTabChange}
				/>
			)}

			<div
				id={iframeWraperId}
				style={{
					display: hideIframe || resultTab !== ResultTabs.RESULT ? 'block' : 'block',
				}}
				className={hideFrame ? 'cwt-result hide-frame' : 'cwt-result'}
			/>

			<div>
				{resultTab === ResultTabs.RESULT && isJsOnly && (
					<div className="cwt-js-result">
						{jsResults && (
							<JsOnlyResult evaluatedResult={jsResults} mode={ResultMode.RESULT} />
						)}
					</div>
				)}
			</div>
			<div>
				{resultTab === ResultTabs.JS_OUTPUT && (
					<div className="cwt-js-result">
						{jsResults && (
							<JsOnlyResult evaluatedResult={jsResults} mode={ResultMode.OUTPUT} />
						)}
						{jsTabResults && (
							<div>
								<JsOnlyResult evaluatedResult={jsTabResults} />
							</div>
						)}
					</div>
				)}
			</div>
			<div>
				{resultTab === ResultTabs.JS_TESTS && (
					<div className="cwt-js-result">
						{hasJsTests && <JsTestsResult evaluatedResult={jsResults} />}
					</div>
				)}
			</div>
		</div>
	);
};

export default CodeResult;
